import React, {useEffect, useRef} from 'react'

import {postMessageToParent} from '../../postMessageToParent'

interface LinkInterceptorProps {
  containerRef?: React.RefObject<HTMLElement>
}

/**
 * Component that intercepts link clicks within a container (or document) and posts messages
 * to the parent window when links would open in a new tab or navigate to external URLs.
 * This is useful for embedded mode scenarios where links need to be handled by the parent frame.
 * @param containerRef - Optional ref to a container element. If provided, only links within
 *                       this container will be intercepted. If not provided, all links in the
 *                       document will be intercepted.
 * @example
 * ```tsx
 * const containerRef = useRef<HTMLDivElement>(null)
 * return (
 *   <div ref={containerRef}>
 *     <LinkInterceptor containerRef={containerRef} />
 *     <a href="https://example.com" target="_blank">External Link</a>
 *   </div>
 * )
 * ```
 */
export const LinkInterceptor = ({containerRef}: LinkInterceptorProps) => {
  const originalWindowOpen = useRef<WindowProxy['open']>()
  useEffect(() => {
    originalWindowOpen.current = window.open

    window.open = function (
      url?: string | URL,
      target?: string,
      features?: string
    ) {
      // Check if the call originated from within our container
      const {activeElement} = document
      const container = containerRef?.current

      if (container && activeElement && container.contains(activeElement)) {
        // Apply same filtering logic as link clicks
        if (url) {
          const href = url.toString()
          const isLocalRedirect =
            href.includes('r.vio') || href.includes('r.e2e.vio')
          const isProviderRedirect = href.includes('/ProviderRedirect')

          if (isLocalRedirect) {
            // Allow local redirects to proceed normally
            return (
              originalWindowOpen.current?.call(window, url, target, features) ||
              null
            )
          }

          if (!isSameOrigin(href) || isProviderRedirect) {
            // Intercept external URLs and provider redirects
            postMessageToParent(href)
            return null
          }
        }
      }

      // Call original window.open for other cases
      return (
        originalWindowOpen.current?.call(window, url, target, features) || null
      )
    }

    // Intercept click events on links
    const handleClick: EventListener = event => {
      const target = event.target as HTMLElement
      const link = target.closest('a')

      if (!link) return

      const {href} = link
      const linkTarget = link.target
      const isLocalRedirect =
        href.includes('r.vio') || href.includes('r.e2e.vio')
      const isProviderRedirect = href.includes('/ProviderRedirect')

      if (isLocalRedirect) return

      if (
        linkTarget === '_blank' ||
        !isSameOrigin(href) ||
        isProviderRedirect
      ) {
        event.preventDefault()
        postMessageToParent(href)
      }
    }

    // Determine where to attach the event listener
    // Read containerRef.current inside the effect to get the latest value
    // React guarantees refs are set synchronously during render, so by the time
    // useEffect runs, containerRef.current should be populated if the element exists
    const eventTarget = containerRef?.current || document
    eventTarget.addEventListener('click', handleClick, true)

    // Cleanup
    return () => {
      // Restore original window.open
      if (originalWindowOpen.current) {
        window.open = originalWindowOpen.current
      }
      // Re-read containerRef.current during cleanup to ensure we remove from the correct target
      const cleanupTarget = containerRef?.current || document
      cleanupTarget.removeEventListener('click', handleClick, true)
    }
  }, [containerRef])

  const isSameOrigin = (url: string): boolean => {
    try {
      const urlObj = new URL(url, window.location.origin)
      return urlObj.origin === window.location.origin
    } catch {
      return false
    }
  }

  return null
}
